import { defineStore } from "pinia";
import { computed, nextTick, ref, watch } from "vue";
import { useAuthUserStore } from "@/stores/authUserStore";
import {
    useProductSearchFilterConfigurationStore
} from "@/stores/productSearch/productSearchFilterConfigurationStore";
import {
    useProductSearchGlobalIsLoadingStore
} from "@/stores/productSearch/productSearchGlobalIsLoadingStore";
import {
    useProductSearchResultsStore
} from "@/stores/productSearch/productSearchResultsStore";
import { StorageHelper } from "@/utils/StorageHelper";
import { StorageName } from "@/utils/StorageHelper/constants";

export interface ComboBoxItem {
    label: string;
    value: number;
}

export const useProductSearchActiveBusinessUnit = defineStore("productSearchActiveBusinessUnit", () => {
    const authUserStore = useAuthUserStore();
    const filterConfigStore = useProductSearchFilterConfigurationStore();
    const globalLoadingStore = useProductSearchGlobalIsLoadingStore();
    const resultsStore = useProductSearchResultsStore();

    const {
        read,
        write,
        remove
    } = new StorageHelper(StorageName.ActiveBusinessUnit);

    const activeBusinessUnit = ref<ComboBoxItem[]>([]);
    const isInitialized = ref<boolean>(false);

    // The searchable business units come from canSeeProducts (workspaces), not from businessUnits
    // (roles): an EHS user has only the EHS role, which has no products, but may see the products
    // of other business units through workspaces.
    const searchableBusinessUnits = computed(() => authUserStore.authUser?.canSeeProducts ?? []);

    const businessUnitOptions = computed(() => {
        if (!searchableBusinessUnits.value.length) return [];

        return searchableBusinessUnits.value.map(({
            id,
            name
        }) => ({
            label: name,
            value: id
        })) as unknown[];
    });

    const showBusinessUnitOptions = computed(() => businessUnitOptions.value.length > 1);

    const activeBusinessUnitId = computed(() => {
        if (!activeBusinessUnit.value.length) return undefined;

        return activeBusinessUnit.value[0]?.value;
    });

    const activeBusinessUnitName = computed(() => {
        if (!activeBusinessUnit.value.length) return undefined;

        return activeBusinessUnit.value[0]?.label;
    });

    const activeBusinessUnitFilterParam = computed(() => {
        if (!activeBusinessUnit.value.length) return undefined;

        return [activeBusinessUnit.value[0]?.label];
    });


    const updateBusinessUnitConfiguration = async () => {
        if (!activeBusinessUnitId.value) return;

        await filterConfigStore.retrieve(activeBusinessUnitId.value);
    };

    watch(activeBusinessUnit, async () => {
        if (!isInitialized.value) return;

        write(activeBusinessUnit.value);

        await globalLoadingStore.setLoadingState();
        await updateBusinessUnitConfiguration();
        await resultsStore.fetchResults();
        await globalLoadingStore.resetLoadingState();
    },);

    const isSearchableForUser = ({ value }: ComboBoxItem) => {
        return searchableBusinessUnits.value.some(({ id }) => id === value);
    };

    const restoreFromStorage = () => {
        const storedBusinessUnit = read<ComboBoxItem[]>();

        if (!storedBusinessUnit) return;

        // The stored business unit may predate a change of the user's workspaces.
        if (storedBusinessUnit.length && !isSearchableForUser(storedBusinessUnit[0])) return;

        activeBusinessUnit.value = storedBusinessUnit;
    };

    // Prefers the user's main business unit when it is searchable, so users of one business unit keep
    // their default - otherwise (e.g. EHS) the first searchable one.
    const setDefaultBusinessUnit = () => {
        const mainBusinessUnitId = authUserStore.mainBusinessUnit?.id;
        const defaultBusinessUnit = searchableBusinessUnits.value.find(({ id }) => id === mainBusinessUnitId)
            ?? searchableBusinessUnits.value[0];

        if (!defaultBusinessUnit) return;

        activeBusinessUnit.value = [{
            label: defaultBusinessUnit.name,
            value: defaultBusinessUnit.id
        }];
    };

    const setInitialValue = async () => {
        if (isInitialized.value) return;

        restoreFromStorage();

        await nextTick();

        if (!activeBusinessUnitId.value) {
            setDefaultBusinessUnit();
        }
    };


    const initialize = async () => {
        await setInitialValue();

        await nextTick();

        // Only lock in initialization once a real business unit could be established.
        // If activeBusinessUnit is still empty (e.g. authUser/business units haven't
        // loaded yet), leave isInitialized false so a later initialize() call can retry.
        if (activeBusinessUnitId.value !== undefined) {
            isInitialized.value = true;
        }
    };

    // Set isInitialized false BEFORE clearing activeBusinessUnit so the watch() above
    // guard-skips its config/results fetch cascade — this is a logout-time cleanup,
    // not a real business-unit change that should trigger a re-fetch.
    const reset = () => {
        isInitialized.value = false;
        activeBusinessUnit.value = [];
        remove();
    };

    return {
        activeBusinessUnit,
        activeBusinessUnitName,
        activeBusinessUnitId,
        activeBusinessUnitFilterParam,
        businessUnitOptions,
        showBusinessUnitOptions,
        initialize,
        reset
    };
});